import type { DestinationsContainer } from '@elara/client-core/containers/DestinationsContainer';
import type { Destination } from '@elara/client-core/models/DestinationModel';

import type { ToolResultRowEnricher } from './types.js';

import { getEmojiFlag } from '@elara/client-core/lib/utils';

import { useApp } from '#states/hooks.js';

function useLoadedDestination(destinationId: string | undefined): Destination | undefined {
  const { features } = useApp('features');
  const destinations = (features as unknown as { destinations?: DestinationsContainer }).destinations;
  const handle = destinationId && destinations ? destinations.ClientDestination.get({ id: destinationId }) : undefined;
  if (!handle?.isReady || handle.error) return undefined;
  return handle.data;
}

/**
 * Destination rows render the country emoji flag as the leading icon and the
 * country name (when distinct from the destination's display name) as the subtitle.
 */
export const destinationRowEnricher: ToolResultRowEnricher<Destination> = {
  useModel: useLoadedDestination,
  resolveTitle: ({ model, item }) => model?.displayName ?? model?.name ?? item.title,
  resolveSubtitle: ({ model, item }) => {
    const title = model?.displayName ?? model?.name ?? item.title;
    const countryName = model?.location?.countryName;
    if (countryName && countryName !== title) return countryName;
    return item.subtitle;
  },
  renderIcon: ({ model }) => {
    const code = model?.location?.countryCode;
    const flag = getEmojiFlag(code);
    if (!flag || flag === '🏳️') return undefined;
    return (
      <span className="shrink-0 w-4 h-4 inline-flex items-center justify-center text-sm leading-none" aria-hidden>
        {flag}
      </span>
    );
  },
};
